import React, { useState, useMemo, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box } from '@mui/material';

// Import theme
import getTheme from './theme';

// Import contexts
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';

// Import components
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Import pages
import Home from './pages/Home';
import About from './pages/About';
import Programs from './pages/Programs';
import Contact from './pages/Contact';
import Donate from './pages/Donate';
import ThankYou from './pages/ThankYou';
import NotFound from './pages/NotFound';

// Language-aware wrapper component
const LanguageWrapper = ({ children }) => {
  const { lang } = useParams();
  const { changeLanguage, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Validate language parameter
    if (lang && ['en', 'fr'].includes(lang)) {
      if (currentLanguage !== lang) {
        changeLanguage(lang);
      }
    } else if (lang && !['en', 'fr'].includes(lang)) {
      // Redirect to default language if invalid language is provided
      const pathWithoutLang = location.pathname.replace(`/${lang}`, '');
      navigate(`/en${pathWithoutLang}`, { replace: true });
    }
  }, [lang, currentLanguage, changeLanguage, navigate, location]);

  return children;
};

// AppRoutes component to handle language-aware routing
const AppRoutes = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Handle root route redirect
  useEffect(() => {
    if (location.pathname === '/') {
      navigate('/en', { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <Routes>
      <Route path="/:lang" element={<LanguageWrapper><Home /></LanguageWrapper>} />
      <Route path="/:lang/about" element={<LanguageWrapper><About /></LanguageWrapper>} />
      <Route path="/:lang/programs" element={<LanguageWrapper><Programs /></LanguageWrapper>} />
      <Route path="/:lang/contact" element={<LanguageWrapper><Contact /></LanguageWrapper>} />
      <Route path="/:lang/donate" element={<LanguageWrapper><Donate /></LanguageWrapper>} />
      <Route path="/:lang/thank-you" element={<LanguageWrapper><ThankYou /></LanguageWrapper>} />
      {/* Catch-all route for undefined routes - Must be last */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

function App() {
  const [mode, setMode] = useState('light');
  
  // Update the theme only if the mode changes
  const theme = useMemo(() => getTheme(mode), [mode]);
  
  // Toggle between light and dark modes
  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  return (
    <LanguageProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Box 
            sx={{ 
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
              bgcolor: 'background.default',
              color: 'text.primary',
            }}
          >
            <Navbar toggleColorMode={toggleColorMode} />
            
            <Box component="main" sx={{ flexGrow: 1 }}>
              <AppRoutes />
            </Box>
            
            <Footer />
          </Box>
        </Router>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App; 